/**
 * PlanCard — Cursor-style plan checklist rendered from the brain's `plan`
 * CUSTOM event. Steps get a state derived from the run's thinkingStep cursor:
 * steps before the cursor are complete, the cursor step is active.
 */

import { CheckCircle2, Circle, Loader2, ListChecks } from "lucide-react";
import { cn } from "@flowstate/utils";
import type { PlanPayload } from "../../engine/cortexStreamReducer";

interface PlanCardProps {
  plan: PlanPayload;
  /** Index into the fixed thinking-step ladder (0..4) from orchestrator-status. */
  currentStep?: number;
  isRunning?: boolean;
}

export function PlanCard({ plan, currentStep, isRunning }: PlanCardProps) {
  if (!plan.steps.length) return null;

  return (
    <div className="rounded-xl border border-border/40 bg-muted/30 backdrop-blur-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30">
        <ListChecks className="h-4 w-4 text-primary" />
        <span className="text-xs font-medium text-foreground/90">Plan</span>
        {plan.complexity && (
          <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground/70">
            {plan.complexity}
          </span>
        )}
      </div>
      <ol className="px-3 py-2 space-y-1.5">
        {plan.steps.map((step, i) => {
          // The thinking ladder is coarser than the plan — map position
          // proportionally so the checklist progresses with the run.
          const approx =
            typeof currentStep === "number" && plan.steps.length > 0
              ? Math.floor((currentStep / 4) * plan.steps.length)
              : undefined;
          const done = approx !== undefined && i < approx;
          const active = isRunning && approx !== undefined && i === approx;
          return (
            <li key={i} className="flex items-start gap-2 text-xs">
              {done ? (
                <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
              ) : active ? (
                <Loader2 className="h-3.5 w-3.5 mt-0.5 text-primary animate-spin shrink-0" />
              ) : (
                <Circle className="h-3.5 w-3.5 mt-0.5 text-muted-foreground/40 shrink-0" />
              )}
              <span
                className={cn(
                  "leading-snug",
                  done && "text-muted-foreground line-through",
                  active && "text-foreground font-medium",
                  !done && !active && "text-muted-foreground",
                )}
              >
                {step}
              </span>
            </li>
          );
        })}
      </ol>
      {plan.reasoning && (
        <p className="px-3 pb-2 text-[11px] text-muted-foreground/70 italic leading-snug">
          {plan.reasoning}
        </p>
      )}
    </div>
  );
}
